/**
 * Minimal fetch wrapper. Same surface as SPECTRE's `useDashboardApi()` so
 * fold-in is just `useApi` -> `useDashboardApi` at imports.
 *
 * Auth: a token getter is registered by the AuthProvider at startup.
 *   - Auth0 mode: getter calls `getAccessTokenSilently()` and returns a JWT.
 *   - Dev mode (no Auth0 configured): getter reads engage_dev_key from
 *     localStorage. The backend's require_auth() rejects the dev key when
 *     AUTH0_DOMAIN is set, so a stale localStorage value can't bypass prod auth.
 */

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";

type TokenGetter = () => Promise<string>;

const defaultGetter: TokenGetter = async () =>
  localStorage.getItem("engage_dev_key") ?? "";

let _getToken: TokenGetter = defaultGetter;

export function setTokenGetter(fn: TokenGetter): void {
  _getToken = fn;
}

export function resetTokenGetter(): void {
  _getToken = defaultGetter;
}

/** Account-id getter — set by AccountProvider. When present, every request
 *  carries `X-Account-Id` so the backend scopes responses to the active
 *  account. Calls before the provider mounts return null → no header,
 *  backend defaults to paybyrd-system. */
let _getAccountId: (() => string | null) | null = null;

export function setAccountIdGetter(fn: () => string | null): void {
  _getAccountId = fn;
}

export interface ApiError extends Error {
  status: number;
  body?: unknown;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  idempotencyKey?: string,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = await _getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
  const acct = _getAccountId?.();
  if (acct) headers["X-Account-Id"] = acct;
  // Dev-only: lets a single browser session act as a specific partner so
  // the portal is demo-able without Auth0. Backend ignores this header
  // when AUTH0_DOMAIN is set (production), so it's safe to leave wired.
  const devPartnerEmail = localStorage.getItem("engage_dev_partner_email");
  if (devPartnerEmail) headers["X-Partner-Email"] = devPartnerEmail;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    const err = new Error(
      (errBody as { detail?: string; error?: string } | null)?.detail ||
        (errBody as { error?: string } | null)?.error ||
        res.statusText,
    ) as ApiError;
    err.status = res.status;
    err.body = errBody;
    throw err;
  }
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown, idempotencyKey?: string) =>
    request<T>("POST", path, body, idempotencyKey),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
};
